import { contractRenewalWorkflowsApi, type ContractRenewalWorkflowDto, type ContractRenewalWorkflowItemDto, type RentalAgreementDto } from '../../services/api';
import { i18nService, t } from '../../services/i18n';
import { ApiError } from '../../services/core.api';
import { toastService } from '../../services/toast';
import { openEditAgreementDialog } from './property-details-dialogs';
import { PROPERTY_ICONS, escapeHtml } from './property-shared';

const RENEWAL_STEP_LABELS: Record<string, string> = {
  ExtendAgreement: 'property.renewals.step.extendAgreement',
  ContactTenant: 'property.renewals.step.contactTenant',
  MarkUpcomingVacancy: 'property.renewals.step.markUpcomingVacancy',
};

const RENEWAL_STEP_HINTS: Record<string, string> = {
  ExtendAgreement: 'property.renewals.step.extendAgreementHint',
  ContactTenant: 'property.renewals.step.contactTenantHint',
  MarkUpcomingVacancy: 'property.renewals.step.markUpcomingVacancyHint',
};

export class RcPropertyContractRenewalsTab extends HTMLElement {
  propertyId = '';
  propertyAddress = '';
  agreements: RentalAgreementDto[] = [];

  private workflows: ContractRenewalWorkflowDto[] = [];
  private loadedPropertyId = '';
  private loading = false;
  private error = '';
  private busyKey = '';

  connectedCallback(): void {
    this.addEventListener('click', this.handleClick);
    this.render();
  }

  disconnectedCallback(): void {
    this.removeEventListener('click', this.handleClick);
  }

  render(): void {
    if (this.propertyId && this.loadedPropertyId !== this.propertyId && !this.loading) {
      void this.load();
    }

    const toolbar = `
      <div class="tab-toolbar turnover-toolbar">
        <div>
          <strong>${t('property.renewals.title')}</strong>
          <p class="turnover-toolbar-copy">${t('property.renewals.subtitle')}</p>
        </div>
      </div>
    `;

    if (this.loading && this.loadedPropertyId !== this.propertyId) {
      this.innerHTML = `${toolbar}<div class="empty-state"><p>${t('common.loading')}</p></div>`;
      return;
    }

    if (this.error) {
      this.innerHTML = `${toolbar}<div class="empty-state"><p>${escapeHtml(this.error)}</p></div>`;
      return;
    }

    if (this.workflows.length === 0) {
      this.innerHTML = `${toolbar}<div class="empty-state turnover-empty"><p>${t('property.renewals.empty')}</p></div>`;
      return;
    }

    this.innerHTML = `
      ${toolbar}
      <div class="turnover-list renewal-list">
        ${this.workflows.map((workflow) => this.renderWorkflow(workflow)).join('')}
      </div>
    `;
  }

  private async load(): Promise<void> {
    if (!this.propertyId) {
      return;
    }

    this.loading = true;
    this.error = '';
    this.render();

    try {
      this.workflows = await contractRenewalWorkflowsApi.getByProperty(this.propertyId);
      this.loadedPropertyId = this.propertyId;
      this.dispatchCountChange();
    } catch (error) {
      console.error('Failed to load contract renewal workflows:', error);
      this.error = error instanceof Error ? error.message : t('property.renewals.loadError');
    } finally {
      this.loading = false;
      this.render();
    }
  }

  private renderWorkflow(workflow: ContractRenewalWorkflowDto): string {
    const completedCount = workflow.items.filter((item) => item.isCompleted).length;
    const stageBadgeClass = workflow.stage === 'WithinThreeMonths' ? 'badge-warning' : 'badge-info';

    return `
      <article class="turnover-card renewal-card" data-workflow-id="${workflow.id}">
        <div class="turnover-card-header">
          <div>
            <h3>${escapeHtml(workflow.tenantName)}</h3>
            <div class="turnover-card-meta">
              <span class="badge ${stageBadgeClass}">${t(workflow.stage === 'WithinThreeMonths' ? 'property.renewals.stage.threeMonths' : 'property.renewals.stage.sixMonths')}</span>
              <span>${t('property.renewals.endsOn')} ${escapeHtml(i18nService.formatDate(workflow.endDate))}</span>
              <span class="badge ${completedCount > 0 ? 'badge-success' : 'badge-muted'}">${completedCount}/${workflow.items.length}</span>
            </div>
          </div>
          <div class="renewal-card-summary">
            <strong>${escapeHtml(this.describeDaysRemaining(workflow.daysRemaining))}</strong>
            <span>${escapeHtml(this.propertyAddress || workflow.propertyAddress)}</span>
          </div>
        </div>
        <div class="turnover-step-list renewal-step-list">
          ${workflow.items.map((item) => this.renderItem(workflow, item)).join('')}
        </div>
      </article>
    `;
  }

  private renderItem(workflow: ContractRenewalWorkflowDto, item: ContractRenewalWorkflowItemDto): string {
    const busy = this.busyKey === `${workflow.id}:${item.step}`;
    const isAutomatic = item.step === 'ExtendAgreement';
    const actionLabel = t(item.step === 'ExtendAgreement'
      ? 'property.renewals.action.extendAgreement'
      : item.step === 'ContactTenant'
        ? 'property.renewals.action.contactTenant'
        : 'property.renewals.action.markUpcomingVacancy');

    return `
      <div class="turnover-step-row renewal-step-row ${item.isCompleted ? 'is-complete' : ''}">
        <div class="turnover-step-copy">
          <span class="turnover-step-label">${t(RENEWAL_STEP_LABELS[item.step] || item.step)}</span>
          <span class="turnover-step-meta">${t(RENEWAL_STEP_HINTS[item.step] || item.step)}</span>
          ${item.completedAtUtc ? `<span class="turnover-step-meta">${t('property.turnovers.completedAt')} ${escapeHtml(i18nService.formatDate(item.completedAtUtc))}</span>` : ''}
        </div>
        <div class="turnover-step-actions renewal-step-actions">
          ${item.isCompleted ? `<span class="badge badge-success">${t('common.completed')}</span>` : ''}
          ${isAutomatic || !item.isCompleted ? `
            <button
              class="btn ${isAutomatic ? 'btn-primary' : 'btn-outline'} btn-sm"
              data-action="${isAutomatic ? 'extend-agreement' : 'toggle-step'}"
              data-workflow-id="${workflow.id}"
              data-step="${item.step}"
              data-agreement-id="${workflow.rentalAgreementId}"
              ${busy ? 'disabled' : ''}>
              ${actionLabel}
            </button>
          ` : ''}
          ${!isAutomatic && item.isCompleted ? `
            <button
              class="btn btn-outline btn-sm"
              data-action="undo-step"
              data-workflow-id="${workflow.id}"
              data-step="${item.step}"
              ${busy ? 'disabled' : ''}>
              ${t('common.undo')}
            </button>
          ` : ''}
        </div>
      </div>
    `;
  }

  private handleClick = async (event: Event): Promise<void> => {
    const target = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-action]');
    if (!target) {
      return;
    }

    const workflowId = target.dataset.workflowId;
    const step = target.dataset.step;
    const agreementId = target.dataset.agreementId;

    switch (target.dataset.action) {
      case 'extend-agreement':
        if (agreementId) {
          await this.openExtendAgreementDialog(agreementId);
        }
        return;
      case 'toggle-step':
        if (workflowId && step) {
          await this.updateStep(workflowId, step, true);
        }
        return;
      case 'undo-step':
        if (workflowId && step) {
          await this.updateStep(workflowId, step, false);
        }
        return;
      default:
        return;
    }
  };

  private async openExtendAgreementDialog(agreementId: string): Promise<void> {
    const agreement = this.agreements.find((item) => item.id === agreementId);
    if (!agreement) {
      return;
    }

    openEditAgreementDialog(agreement, async () => {
      this.dispatchEvent(new CustomEvent('property-contract-renewals-changed', { bubbles: true }));
    });
  }

  private async updateStep(workflowId: string, step: string, isCompleted: boolean): Promise<void> {
    const busyKey = `${workflowId}:${step}`;
    this.busyKey = busyKey;
    this.render();

    try {
      const note = isCompleted && step === 'ContactTenant'
        ? await this.promptForNote()
        : undefined;

      if (isCompleted && step === 'ContactTenant' && note === null) {
        return;
      }

      const updated = await contractRenewalWorkflowsApi.updateStep(workflowId, step, {
        isCompleted,
        note: note || undefined,
      });
      this.replaceWorkflow(updated);
      this.error = '';
    } catch (error) {
      console.error('Failed to update contract renewal workflow step:', error);
      this.error = error instanceof ApiError ? error.message : t('property.renewals.updateError');
      toastService.error(this.error);
    } finally {
      this.busyKey = '';
      this.render();
    }
  }

  private async promptForNote(): Promise<string | null> {
    return await new Promise<string | null>((resolve) => {
      const overlay = document.createElement('div');
      overlay.className = 'modal-overlay';
      overlay.innerHTML = `
        <div class="modal">
          <div class="modal-header">
            <h2>${t('property.renewals.contactModalTitle')}</h2>
            <button class="modal-close" data-action="close">${PROPERTY_ICONS.close}</button>
          </div>
          <p class="modal-subtitle">${escapeHtml(this.propertyAddress)}</p>
          <form>
            <div class="form-group">
              <label>${t('property.renewals.contactModalNote')}</label>
              <textarea name="note" rows="4" placeholder="${t('property.renewals.contactModalPlaceholder')}"></textarea>
            </div>
            <div class="modal-actions">
              <button type="button" class="btn btn-outline" data-action="cancel">${t('common.cancel')}</button>
              <button type="submit" class="btn btn-primary">${t('common.save')}</button>
            </div>
          </form>
        </div>
      `;

      const close = (value: string | null): void => {
        overlay.remove();
        resolve(value);
      };

      overlay.addEventListener('click', (evt) => { if (evt.target === overlay) close(null); });
      overlay.querySelector('[data-action="close"]')?.addEventListener('click', () => close(null));
      overlay.querySelector('[data-action="cancel"]')?.addEventListener('click', () => close(null));
      overlay.querySelector('form')?.addEventListener('submit', (evt) => {
        evt.preventDefault();
        const formData = new FormData(evt.target as HTMLFormElement);
        close(((formData.get('note') as string) || '').trim());
      });

      document.body.appendChild(overlay);
    });
  }

  private replaceWorkflow(updated: ContractRenewalWorkflowDto): void {
    this.workflows = this.workflows.map((item) => item.id === updated.id ? updated : item);
  }

  private describeDaysRemaining(daysRemaining: number): string {
    if (daysRemaining === 0) return t('common.today');
    if (daysRemaining === 1) return t('common.daysRemaining', { count: 1 });
    return t('common.daysRemaining', { count: daysRemaining });
  }

  private dispatchCountChange(): void {
    this.dispatchEvent(new CustomEvent('property-renewal-count-changed', {
      bubbles: true,
      detail: { count: this.workflows.length },
    }));
  }
}

if (!customElements.get('rc-property-contract-renewals-tab')) {
  customElements.define('rc-property-contract-renewals-tab', RcPropertyContractRenewalsTab);
}
